import React from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { docRowScopeChoice } from '../../../../utils/variableScope';
import { rowKey } from '../utils/tableUtils';
import SortableVariableRow from './SortableVariableRow';
import VariableRow from './VariableRow';

const VariablesTableBody = ({
  isEmpty,
  isSingleRow,
  visibleInherited,
  visibleEditable,
  inheritedEdit,
  paintEditableRow,
  conflictFor,
  showScopeSelect,
  scopeOptions,
  onPatch,
  onRename,
  onScopeChange,
  onTypeChange,
  onDelete,
  onToggleLock,
  resolveVariable,
  pickerVariables,
  onCreateVariable,
  historyEpoch,
  missionVariables = null,
  newRowIdRef,
  sensors,
  sortableIds,
  handleDragEnd,
  pathSlashOffers = [],
  onUndoPathSlash = null,
  commitPathSlash = null,
}) => (
  <div className="variables-panel__table-body">
    {isEmpty ? (
      <div className="variables-panel__empty">No variables yet</div>
    ) : (
      <>
        {visibleInherited.map((raw) => {
          const variable = inheritedEdit.displayVariable(raw);
          const inhKey = inheritedEdit.keyOf(variable);
          const unlocked = inheritedEdit.isUnlocked(variable);
          return (
            <VariableRow
              key={rowKey('inherited', variable.id, variable.sourceId)}
              variable={variable}
              scope="inherited"
              sourceName={variable.sourceName}
              conflictSource={null}
              readOnly
              canEditInheritedValue={inheritedEdit.canEditInherited}
              inheritedValueUnlocked={unlocked}
              onRequestInheritedValueEdit={() =>
                inheritedEdit.requestEdit(variable)
              }
              autoFocusValue={inheritedEdit.focusKey === inhKey}
              onValueFocused={inheritedEdit.clearFocus}
              showScopeSelect={false}
              scopeOptions={scopeOptions}
              onPatch={onPatch}
              onPatchInheritedValue={(patch, opts) => {
                const route = (next, nextOpts) =>
                  inheritedEdit.patchValue(variable, next, nextOpts);
                if (commitPathSlash) {
                  commitPathSlash('inherited', variable.id, patch, opts, route);
                  return;
                }
                route(patch, opts);
              }}
              pathSlashOffers={pathSlashOffers.filter(
                (offer) => offer.scope === 'inherited' && offer.variableId === variable.id
              )}
              onUndoPathSlash={onUndoPathSlash}
              onScopeChange={onScopeChange}
              onTypeChange={onTypeChange}
              onDelete={onDelete}
              onToggleLock={null}
              resolveVariable={resolveVariable}
              pickerVariables={pickerVariables}
              historyEpoch={historyEpoch}
            />
          );
        })}
        {isSingleRow ? (
          visibleEditable.map(({ variable, scope }) => (
            <VariableRow
              key={rowKey(scope, variable.id)}
              variable={paintEditableRow(variable, scope)}
              scope={scope}
              scopeChoice={
                scope === 'campaign' ? 'campaign' : docRowScopeChoice(variable, missionVariables)
              }
              conflictSource={conflictFor(variable, scope)}
              readOnly={false}
              showScopeSelect={showScopeSelect}
              scopeOptions={scopeOptions}
              onPatch={onPatch}
              onRename={onRename}
              onScopeChange={onScopeChange}
              onTypeChange={onTypeChange}
              onDelete={onDelete}
              onToggleLock={onToggleLock}
              resolveVariable={resolveVariable}
              pickerVariables={pickerVariables}
              onCreateVariable={onCreateVariable}
              historyEpoch={historyEpoch}
              pathSlashOffers={pathSlashOffers.filter(
                (offer) => offer.scope === scope && offer.variableId === variable.id
              )}
              onUndoPathSlash={onUndoPathSlash}
            />
          ))
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableIds}
              strategy={verticalListSortingStrategy}
            >
              {visibleEditable.map(({ variable, scope }) => (
                <SortableVariableRow
                  key={rowKey(scope, variable.id)}
                  variable={paintEditableRow(variable, scope)}
                  scope={scope}
                  scopeChoice={
                    scope === 'campaign'
                      ? 'campaign'
                      : docRowScopeChoice(variable, missionVariables)
                  }
                  conflictSource={conflictFor(variable, scope)}
                  readOnly={false}
                  showScopeSelect={showScopeSelect}
                  scopeOptions={scopeOptions}
                  onPatch={onPatch}
                  onRename={onRename}
                  onScopeChange={onScopeChange}
                  onTypeChange={onTypeChange}
                  onDelete={onDelete}
                  onToggleLock={onToggleLock}
                  autoFocus={variable.id === newRowIdRef.current}
                  resolveVariable={resolveVariable}
                  pickerVariables={pickerVariables}
                  onCreateVariable={onCreateVariable}
                  historyEpoch={historyEpoch}
                  pathSlashOffers={pathSlashOffers.filter(
                    (offer) => offer.scope === scope && offer.variableId === variable.id
                  )}
                  onUndoPathSlash={onUndoPathSlash}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </>
    )}
  </div>
);

export default VariablesTableBody;
